import { DbError, type AttachmentWork, type BackupExport, type BackupFile, type BackupSummary, type Bind, type DbInfo, type DbMode, type DbRequest, type RequestEnvelope, type Row, type WorkerMessage } from './protocol';
import type { ApplyResult, PushedKey, SyncRecord } from './syncOps';

export type ChangeSource = 'local' | 'remote';

type Pending = { resolve: (value: unknown) => void; reject: (error: Error) => void };

/** Typed RPC client for the DB worker. One instance per open database. */
export class DbClient {
  private readonly worker: Worker;
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();
  private readonly listeners = new Set<(tables: string[], source: ChangeSource) => void>();

  constructor() {
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'renalflow-db' });
    this.worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const message = event.data;
      if (message.kind === 'tables-changed') {
        this.listeners.forEach((listener) => listener(message.tables, message.source));
        return;
      }
      const pending = this.pending.get(message.id);
      if (!pending) return;
      this.pending.delete(message.id);
      if (message.ok) pending.resolve(message.result);
      else pending.reject(new DbError(message.error.code, message.error.message));
    };
    this.worker.onerror = (event) => {
      const error = new DbError('INTERNAL', event.message || 'The database worker crashed.');
      this.pending.forEach((pending) => pending.reject(error));
      this.pending.clear();
    };
  }

  private send<T>(request: DbRequest): Promise<T> {
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
      this.worker.postMessage({ id, request } satisfies RequestEnvelope);
    });
  }

  open(mode: DbMode, userId?: string) {
    return this.send<null>({ type: 'open', mode, userId });
  }

  /** Close the account database and delete its files. The client must be terminated afterwards. */
  destroy() {
    return this.send<null>({ type: 'destroy' });
  }

  pendingChanges() {
    return this.send<number>({ type: 'pendingChanges' });
  }

  collectDirty(limit: number) {
    return this.send<SyncRecord[]>({ type: 'collectDirty', limit });
  }

  markPushed(keys: PushedKey[]) {
    return this.send<null>({ type: 'markPushed', keys });
  }

  applyRemote(records: SyncRecord[]) {
    return this.send<ApplyResult>({ type: 'applyRemote', records });
  }

  query<T extends object = Row>(sql: string, bind?: Bind) {
    return this.send<T[]>({ type: 'query', sql, bind });
  }

  insert<T extends object = Row>(table: string, values: Row) {
    return this.send<T>({ type: 'insert', table, values });
  }

  update<T extends object = Row>(table: string, id: string, patch: Row) {
    return this.send<T>({ type: 'update', table, id, patch });
  }

  upsert<T extends object = Row>(table: string, id: string, values: Row) {
    return this.send<T>({ type: 'upsert', table, id, values });
  }

  softDelete(table: string, id: string) {
    return this.send<null>({ type: 'softDelete', table, id });
  }

  getDeviceSetting(key: string) {
    return this.send<string | null>({ type: 'getDeviceSetting', key });
  }

  setDeviceSetting(key: string, value: string | null) {
    return this.send<null>({ type: 'setDeviceSetting', key, value });
  }

  setReadOnly(readOnly: boolean) {
    return this.send<null>({ type: 'setReadOnly', readOnly });
  }

  info() {
    return this.send<DbInfo>({ type: 'info' });
  }

  putAttachment(values: { ownerTable: string; ownerId: string; mime: string; fileName: string | null; bytes: Uint8Array<ArrayBuffer> }) {
    return this.send<Row>({ type: 'putAttachment', ...values });
  }

  readAttachment(id: string) {
    return this.send<Uint8Array<ArrayBuffer> | null>({ type: 'readAttachment', id });
  }

  storeAttachment(id: string, bytes: Uint8Array<ArrayBuffer>) {
    return this.send<null>({ type: 'storeAttachment', id, bytes });
  }

  deleteAttachment(id: string) {
    return this.send<null>({ type: 'deleteAttachment', id });
  }

  attachmentWork() {
    return this.send<AttachmentWork>({ type: 'attachmentWork' });
  }

  attachmentUploaded(id: string) {
    return this.send<null>({ type: 'attachmentUploaded', id });
  }

  attachmentRemoved(id: string) {
    return this.send<null>({ type: 'attachmentRemoved', id });
  }

  exportBackup() {
    return this.send<BackupExport>({ type: 'exportBackup' });
  }

  /** Opens and migrates a backup for import; replace with applyBackup() or drop with discardBackup(). */
  openBackup(bytes: Uint8Array<ArrayBuffer>) {
    return this.send<BackupSummary>({ type: 'openBackup', bytes });
  }

  applyBackup(files: BackupFile[]) {
    return this.send<null>({ type: 'applyBackup', files });
  }

  discardBackup() {
    return this.send<null>({ type: 'discardBackup' });
  }

  onTablesChanged(listener: (tables: string[], source: ChangeSource) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Terminating the worker releases the OPFS sync access handles so another tab can open the DB. */
  terminate() {
    this.worker.terminate();
    const error = new DbError('NOT_OPEN', 'Database was closed.');
    this.pending.forEach((pending) => pending.reject(error));
    this.pending.clear();
    this.listeners.clear();
  }
}
